import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ================================================================
   Nexus One Platform Notice — /legal/platform-notice
   Renders the canonical 25-section legal notice from backend.
   ================================================================ */

export default function PlatformNotice() {
    const [data, setData] = useState(null);
    const [err, setErr]   = useState(null);

    useEffect(() => {
        axios.get(`${API_BASE}/legal/disclaimers`)
            .then((r) => setData(r.data))
            .catch(() => setErr("Failed to load Platform Notice."));
    }, []);

    return (
        <div className="min-h-screen atlas-hero-bg py-16 px-6">
            <div className="max-w-3xl mx-auto">
                <Link
                    to="/"
                    data-testid="platform-notice-back"
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium mb-8"
                    style={{ color: "var(--atlas-text-secondary)" }}
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to Nexus One
                </Link>

                <div className="atlas-hero-eyebrow mb-4">
                    <Shield className="w-3 h-3" strokeWidth={2.5} />
                    Enterprise Notice · Attorney review recommended
                </div>

                {err && (
                    <div
                        data-testid="platform-notice-error"
                        className="atlas-card p-5"
                        style={{ borderColor: "rgba(239,68,68,0.4)" }}
                    >
                        <AlertTriangle className="w-4 h-4" style={{ color: "#fca5a5" }} strokeWidth={2} />
                        <div className="mt-2 text-[14px]" style={{ color: "var(--atlas-text-secondary)" }}>
                            {err}
                        </div>
                    </div>
                )}

                {!data && !err && (
                    <div className="atlas-card p-8 animate-pulse" style={{ minHeight: 320 }}>
                        <div className="h-8 w-3/4 rounded" style={{ background: "var(--atlas-surface-alt)" }} />
                        <div className="mt-4 h-4 w-full rounded" style={{ background: "var(--atlas-surface-alt)" }} />
                        <div className="mt-2 h-4 w-4/5 rounded" style={{ background: "var(--atlas-surface-alt)" }} />
                    </div>
                )}

                {data && (
                    <article className="atlas-card p-8" data-testid="platform-notice-article">
                        <h1
                            className="atlas-text-gradient text-[32px] sm:text-[38px] font-bold tracking-tight leading-tight"
                            data-testid="platform-notice-title"
                        >
                            {data.platform_notice.title}
                        </h1>
                        <div className="mt-3 flex items-center gap-3 text-[11px] uppercase tracking-[0.14em]"
                             style={{ color: "var(--atlas-text-muted)" }}>
                            <span data-testid="platform-notice-version">Version {data.platform_notice.version}</span>
                            <span>·</span>
                            <span>Updated {data.platform_notice.updated}</span>
                        </div>

                        <p
                            className="mt-5 text-[14.5px] leading-relaxed"
                            style={{ color: "var(--atlas-text-secondary)" }}
                            data-testid="platform-notice-preamble"
                        >
                            {data.platform_notice.preamble}
                        </p>

                        <div
                            className="mt-6 border-t pt-6 space-y-6"
                            style={{ borderColor: "var(--atlas-border-strong)" }}
                            data-testid="platform-notice-sections"
                        >
                            {data.platform_notice.sections.map((s, i) => (
                                <section key={i} data-testid={`platform-notice-section-${i}`}>
                                    <h2
                                        className="text-[16px] font-semibold"
                                        style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.01em" }}
                                    >
                                        {s.heading}
                                    </h2>
                                    <p
                                        className="mt-1.5 text-[13.5px] leading-relaxed"
                                        style={{ color: "var(--atlas-text-secondary)" }}
                                    >
                                        {s.body}
                                    </p>
                                </section>
                            ))}
                        </div>
                    </article>
                )}
            </div>
        </div>
    );
}
